import { useCallback, useEffect, useState } from "react";
import { ApiResult, DataApiClient } from "../api/dataApiClient";
import { AdminApiClient } from "../api/adminApiClient";
import { LightspeedAuthApiClient } from "../api/lightspeedAuthApiClient";
import {
  useDataApiClient,
  useAdminApiClient,
  useLightspeedAuthApiClient,
} from "./useApiClient";
import { useDidUnmount } from "./useDidUnmount";

export type ApiRequestState<T> = {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  statusCode: number | null;
  next?: string;
  result: ApiResult<T> | null;
  args?: any[];
};

export const useDataApiUpdateRequest = <TResponse, TArgs extends any[]>(
  fn: (
    apiClient: DataApiClient,
    ...args: TArgs
  ) => Promise<ApiResult<TResponse>> | null,
  deps: any[]
) => {
  return useApiUpdateRequest(fn, useDataApiClient(), deps);
};

export const useAdminApiUpdateRequest = <TResponse, TArgs extends any[]>(
  fn: (
    apiClient: AdminApiClient,
    ...args: TArgs
  ) => Promise<ApiResult<TResponse>> | null,
  deps: any[]
) => {
  return useApiUpdateRequest(fn, useAdminApiClient(), deps);
};

export const useLightspeedAuthApiUpdateRequest = <
  TResponse,
  TArgs extends any[]
>(
  fn: (
    apiClient: LightspeedAuthApiClient,
    ...args: TArgs
  ) => Promise<ApiResult<TResponse>> | null,
  deps: any[]
) => {
  return useApiUpdateRequest(fn, useLightspeedAuthApiClient(), deps);
};

export const useApiUpdateRequest = <T, TResponse, TArgs extends any[]>(
  fn: (apiClient: T, ...args: TArgs) => Promise<ApiResult<TResponse>> | null,
  apiClient: T,
  deps: any[]
) => {
  const didUnmount = useDidUnmount();

  const [apiState, setApiState] = useState<ApiRequestState<TResponse>>({
    data: null,
    isLoading: false,
    error: null,
    statusCode: null,
    result: null,
  });

  const request = useCallback(
    (...args: TArgs) => {
      setApiState((state) => ({ ...state, isLoading: true }));
      const requestPromise = fn(apiClient, ...args);

      if (!requestPromise) {
        return;
      }

      requestPromise
        .then((result) => {
          if (didUnmount.current) {
            return;
          }
          if (result.ok) {
            setApiState({
              isLoading: false,
              data: result.data,
              error: null,
              statusCode: result.statusCode,
              next: result.next,
              result,
              args,
            });
          } else {
            setApiState({
              isLoading: false,
              data: null,
              error: `HTTP ${result.statusCode} error`,
              statusCode: result.statusCode ?? null,
              result,
              args,
            });
          }
        })
        .catch((error) => {
          if (didUnmount.current) {
            return;
          }
          setApiState({
            isLoading: false,
            data: null,
            error: "Unknown error.",
            statusCode: null,
            result: null,
            args,
          });
        });
    },
    [didUnmount, apiClient, setApiState, ...deps]
  );

  return { ...apiState, request };
};
